import { paramCase } from 'change-case';
import cloneDeep from 'lodash/cloneDeep.js';

import axios from '@/plugins/axios.js';
import CancelSource, { errorHandler } from '@/utilities/CancelSource.js';

const getType = (mutation, namespace) => {
    const { type } = mutation.payload;
    if (type === 'course' || type === 'course_template') {
        return 'CourseTemplateForm';
    }

    return namespace.toLowerCase().includes('temp') ? 'ActivityTemplates' : 'Activities';
};

const auto_save_type = type => {
    return type === 'course' || type === 'course_template' ? 'courseForm' : 'activities';
};

export async function reorder(store, mutation) {
    const cancel = CancelSource(mutation);

    store.dispatch('AutoSave/setSaving', mutation);

    const namespace = mutation.type.replace(/\/.*/, '');

    const grading_scheme = cloneDeep(store.getters[`${namespace}/get`](mutation.payload.uuid));

    // Check if grading scheme exists in store
    if (!grading_scheme) {
        store.dispatch('AutoSave/setSaved', mutation);
        return;
    }

    // Preserve original uuid for later store updates (we may remove it for API payloads)
    const originalUuid = grading_scheme ? grading_scheme.uuid : undefined;
    const activityUuid = grading_scheme.activity_uuid;

    // Ensure criterias field is always present (required for templates)
    if (!grading_scheme.criterias) {
        grading_scheme.criterias = [];
    }

    // For templates, remove course-specific fields as they're not expected by the backend
    const isTemplate = namespace.toLowerCase().includes('temp');
    if (isTemplate) {
        delete grading_scheme.course_id;
        delete grading_scheme.activity_uuid;
        delete grading_scheme.uuid; // This is used internally for store management but not for API
    }

    const grading_scheme_property = `grading_scheme${namespace.toLowerCase().includes('temp') ? '_template' : ''}_id`;

    const parent_namespace = getType(mutation, namespace);

    const isCourseParent = mutation.payload.type === 'course' || mutation.payload.type === 'course_template';

    const axios_type = grading_scheme.id ? 'put' : 'post';
    // Updating an existing scheme does not change its owner link and must not
    // schedule another course/activity save with an unrelated or absent payload.
    const parent =
        axios_type === 'post'
            ? isCourseParent
                ? cloneDeep(store.getters[`${parent_namespace}/course`])
                : cloneDeep(store.getters[`${parent_namespace}/get`](activityUuid))
            : null;

    // For templates with no criteria, handle appropriately
    if (isTemplate && grading_scheme.criterias.length === 0) {
        if (grading_scheme.id) {
            // Existing template: use the special removeAllCriterias endpoint
            const removeAllRoute = `/api/${paramCase(namespace)}/${grading_scheme.id}/removeAllCriterias`;
            try {
                await axios.delete(removeAllRoute, cancel.axiosConfig);
            } catch (error) {
                // Swallow cancel and properly route other errors to AutoSave store
                errorHandler(mutation)(error, store);
            }
        }
        // For new templates or after deletion, mark as saved and don't proceed with normal save
        store.dispatch('AutoSave/setSaved', mutation);
        return;
    }

    grading_scheme.criterias.forEach(criteria => {
        criteria.minimum /= 100;
    });

    const api_route = `/api/${paramCase(namespace)}${axios_type === 'put' ? `/${grading_scheme.id}` : ''}`;

    let data;
    try {
        const response = await axios[axios_type](`${api_route}`, grading_scheme, cancel.axiosConfig);
        // API returns a JSON:API-like envelope { data: {...} }
        data = response?.data?.data ?? response.data;
    } catch (error) {
        // Swallow cancel and properly route other errors to AutoSave store
        errorHandler(mutation)(error, store);
        store.dispatch('AutoSave/setSaved', mutation);
        return;
    }

    if (axios_type === 'post') {
        parent[grading_scheme_property] = data.id;

        store.dispatch(`${namespace}/updateSchemeIds`, {
            scheme: data,
            uuid: originalUuid,
        });

        // Immediately reflect linkage on the form store so UI updates without waiting
        if (isCourseParent) {
            const formField = grading_scheme_property; // grading_scheme_template_id or grading_scheme_id
            store.commit('CourseTemplateForm/updateCourseField', { field: formField, value: data.id }, { root: true });
        }

        await store.dispatch(`AutoSave/${auto_save_type(mutation.payload.type)}`, {
            mutation: mutation.type,
            [isCourseParent ? 'course' : 'activity']: parent,
        });
    }

    if (axios_type === 'put') {
        // Handle PUT response for updates (including criteria deletions)
        store.dispatch(`${namespace}/updateSchemeIds`, {
            scheme: data,
            uuid: originalUuid,
        });
    }

    store.dispatch('AutoSave/setSaved', mutation);
}
